import React, { useState } from "react";
import "./FinancePage.css";
import { recordManualPayment } from "../../api/paymentsApi";

const METHOD_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "check", label: "Check" },
  { value: "ach", label: "ACH / Bank Transfer" },
  { value: "card", label: "Card (manual entry)" },
  { value: "other", label: "Other" },
];

const CATEGORY_OPTIONS = [
  { value: "full", label: "Full Payment" },
  { value: "deposit", label: "Deposit" },
  { value: "milestone", label: "Milestone" },
  { value: "final", label: "Final Payment" },
  { value: "recurring", label: "Recurring" },
  { value: "adjustment", label: "Adjustment" },
];

const money = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function RecordPaymentModal({ invoice, onClose, onSuccess }) {
  const balanceDue = Number(invoice?.balance_due || 0);
  const [amount, setAmount] = useState(balanceDue > 0 ? balanceDue.toFixed(2) : "");
  const [method, setMethod] = useState("cash");
  const [category, setCategory] = useState("full");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      setError("Amount must be greater than zero.");
      return;
    }
    if (parsedAmount > balanceDue && balanceDue > 0) {
      setError(`Amount ($${money(parsedAmount)}) exceeds balance due ($${money(balanceDue)}).`);
      return;
    }

    setSubmitting(true);
    try {
      await recordManualPayment(invoice.id, {
        amount: parsedAmount.toFixed(2),
        payment_method_type: method,
        payment_category: category,
        external_reference: reference,
        notes,
      });
      if (onSuccess) onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.error || err?.response?.data?.detail || err?.message || "Payment failed.";
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1001, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.4)" }} onClick={onClose} />
      <form
        onSubmit={handleSubmit}
        style={{
          position: "relative", width: "90%", maxWidth: 460, borderRadius: 14,
          border: "1px solid var(--border)",
          background: "var(--panel-glass, var(--glass-bg, #1a1a2e))",  padding: 24,
          boxShadow: "var(--shadow-overlay)",
          display: "flex", flexDirection: "column", gap: 14,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Record Payment</h3>
          <button type="button" className="finance-btn secondary" onClick={onClose}>Cancel</button>
        </div>

        <div className="finance-muted">
          Invoice {invoice.invoice_number} — Balance due: ${money(balanceDue)}
        </div>

        {error && (
          <div style={{ background: "rgba(248,113,113,0.15)", border: "1px solid rgba(248,113,113,0.4)", borderRadius: 8, padding: "8px 12px", color: "#f87171", fontSize: 13 }}>
            {error}
          </div>
        )}

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="finance-muted">Amount</span>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            style={{ height: 36, borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface-secondary, var(--bg-2))", color: "var(--text-main)", padding: "0 10px", fontSize: 15 }}
          />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="finance-muted">Method</span>
            <select value={method} onChange={(e) => setMethod(e.target.value)} style={{ height: 36, borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface-secondary, var(--bg-2))", color: "var(--text-main)", padding: "0 8px" }}>
              {METHOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="finance-muted">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={{ height: 36, borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface-secondary, var(--bg-2))", color: "var(--text-main)", padding: "0 8px" }}>
              {CATEGORY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="finance-muted">Reference (check #, ACH ref, etc.)</span>
          <input
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Check #1042"
            style={{ height: 36, borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface-secondary, var(--bg-2))", color: "var(--text-main)", padding: "0 10px" }}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span className="finance-muted">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Deposit collected after signed proposal"
            style={{ borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface-secondary, var(--bg-2))", color: "var(--text-main)", padding: "8px 10px", resize: "vertical" }}
          />
        </label>

        <button type="submit" className="finance-btn" disabled={submitting} style={{ height: 40, fontSize: 14 }}>
          {submitting ? "Recording..." : `Record ${method === "cash" ? "Cash" : method === "check" ? "Check" : "Payment"}`}
        </button>
      </form>
    </div>
  );
}
